import uuid from 'uuid/v4';

import ErrorHandler from 'store/utils/ErrorHandler';
import Notify from 'store/utils/Notify';
import { getCurrentPage } from 'store/utils/pagination';
import { getTestSet } from 'store/services/testSets/selectors';
import * as api from './api';
import * as types from './actionTypes';
import * as annotationTypes from '../annotations/actionTypes';
import * as selectors from './selectors';

/**
 * Get TestSet Entries
 *
 * @param params
 */
export const getTestSetEntriesList = (params = {}) => async (
  dispatch,
  getState,
) => {
  try {
    const state = getState();
    await dispatch({
      type: types.GET_TEST_SET_ENTRIES_REQUEST,
    });

    const finalParams = {
      ...selectors.getPaginationOptions(state),
      ...params,
    };

    await dispatch({
      type: types.UPDATE_TEST_SET_ENTRIES_PAGINATION_PARAMS,
      payload: {
        paginationOptions: finalParams,
      },
    });

    const testSetId = getTestSet(state).id;

    const response = await api.getTestSetEntriesRequest(testSetId, finalParams);

    await dispatch({
      type: types.GET_TEST_SET_ENTRIES_SUCCESS,
      payload: {
        testSetEntries: response.data.results,
        count: response.data.count,
      },
    });
  } catch (error) {
    await dispatch({
      type: types.GET_TEST_SET_ENTRIES_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'An error occurred',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Add a new TestSet Entry
 *
 * @param data
 */
export const addTestSetEntry = data => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.ADD_TEST_SET_ENTRY_REQUEST,
    });

    const state = getState();
    const testSetId = getTestSet(state).id;

    await api.addTestSetEntryRequest(testSetId, data);

    await dispatch({
      type: types.ADD_TEST_SET_ENTRY_SUCCESS,
    });

    await dispatch(getTestSetEntriesList({ page: 1, value__contains: '' }));

    Notify.success({
      title: 'TestSet Entry was created successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.ADD_TEST_SET_ENTRY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to create Test Set Entry',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Update TestSet Entry
 *
 * @param id
 * @param data
 */
export const updateTestSetEntry = (id, data) => async (dispatch, getState) => {
  try {
    await dispatch({
      type: types.UPDATE_TEST_SET_ENTRY_REQUEST,
    });

    const state = getState();
    const testSetId = getTestSet(state).id;

    await api.updateTestSetEntryRequest(testSetId, id, data);

    await dispatch({
      type: types.UPDATE_TEST_SET_ENTRY_SUCCESS,
    });

    await dispatch(
      getTestSetEntriesList(selectors.getPaginationOptions(state)),
    );

    dispatch({
      type: annotationTypes.RESET_ANNOTATIONS_FOR_TEST_SET_ENTRY,
      payload: {
        testSetEntryId: id,
      },
    });

    Notify.success({
      title: 'TestSet Entry was updated successfully',
    });
  } catch (error) {
    await dispatch({
      type: types.UPDATE_TEST_SET_ENTRY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to update Test Set Entry',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Delete TestSet Entry
 *
 * @param item
 */
export const deleteTestSetEntry = item => async (dispatch, getState) => {
  try {
    const { id, initialValue } = item;

    if (initialValue) {
      await dispatch({
        type: types.DELETE_TEST_SET_ENTRY_REQUEST,
      });

      const state = getState();
      const testSetId = getTestSet(state).id;

      await api.deleteTestSetEntryRequest(testSetId, id);

      await dispatch({
        type: types.DELETE_TEST_SET_ENTRY_SUCCESS,
      });

      await dispatch(
        getTestSetEntriesList({
          page: getCurrentPage(
            selectors.getPaginationOptions(getState()).page,
            selectors.getTestSetEntries(getState()),
          ),
        }),
      );

      Notify.success({
        title: 'TestSet Entry was deleted successfully',
      });
    } else {
      const entries = selectors
        .getTestSetEntriesList(getState())
        .map(o => (o.order > item.order ? { ...o, order: o.order - 1 } : o))
        .filter(entry => entry.id !== id);

      dispatch({
        type: types.UPDATE_TEST_SET_ENTRIES,
        payload: {
          entries,
        },
      });
    }
  } catch (error) {
    await dispatch({
      type: types.DELETE_TEST_SET_ENTRY_ERROR,
      payload: {
        error: error.message,
      },
    });

    Notify.error({
      title: 'Fail to delete Test Set Entry',
      description: ErrorHandler.getNotificationError(error),
    });
  }
};

/**
 * Select entry to edit
 */
export const selectEntryToEdit = id => (dispatch, getState) => {
  const entries = selectors.getTestSetEntriesList(getState()).map(entry => ({
    ...entry,
    isEditable: entry.id === id,
  }));

  dispatch({
    type: types.UPDATE_TEST_SET_ENTRIES,
    payload: {
      entries,
    },
  });
};

/**
 * Deselect entry to edit
 */
export const deselectEntryToEdit = () => (dispatch, getState) => {
  const entries = selectors.getTestSetEntriesList(getState()).map(entry => ({
    ...entry,
    isEditable: false,
  }));

  dispatch({
    type: types.UPDATE_TEST_SET_ENTRIES,
    payload: {
      entries,
    },
  });
};

/**
 * Add new entry before another one
 */
export const addTestSetEntryBefore = order => (dispatch, getState) => {
  const entries = selectors
    .getTestSetEntriesList(getState())
    .reduce((acc, entry) => {
      if (!entry.value) {
        return acc;
      }
      if (entry.order < order) {
        return [...acc, entry];
      }
      if (entry.order === order) {
        return [
          ...acc,
          {
            id: uuid(),
            value: '',
            order: entry.order,
            isEditable: true,
          },
          {
            ...entry,
            order: entry.order + 1,
          },
        ];
      }
      return [
        ...acc,
        {
          ...entry,
          order: entry.order + 1,
        },
      ];
    }, []);

  dispatch({
    type: types.UPDATE_TEST_SET_ENTRIES,
    payload: {
      entries,
    },
  });
};

/**
 * Add new entry after another one
 */
export const addTestSetEntryAfter = order => (dispatch, getState) => {
  const entries = selectors
    .getTestSetEntriesList(getState())
    .reduce((acc, entry) => {
      if (!entry.value) {
        return acc;
      }
      if (entry.order < order) {
        return [...acc, entry];
      }
      if (entry.order === order) {
        return [
          ...acc,
          entry,
          {
            id: uuid(),
            value: '',
            order: entry.order + 1,
            isEditable: true,
          },
        ];
      }
      return [
        ...acc,
        {
          ...entry,
          order: entry.order + 1,
        },
      ];
    }, []);

  dispatch({
    type: types.UPDATE_TEST_SET_ENTRIES,
    payload: {
      entries,
    },
  });
};
